import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  epicsApi,
  type EpicFilters,
  type CreateEpicInput,
  type UpdateEpicInput,
  type ReportGapInput,
} from '@/lib/api/epics';
import { issueKeys } from './use-issues';
import { epicRequestKeys } from './use-epic-requests';

export const epicKeys = {
  all: ['epics'] as const,
  lists: () => [...epicKeys.all, 'list'] as const,
  list: (filters: EpicFilters) => [...epicKeys.lists(), filters] as const,
  details: () => [...epicKeys.all, 'detail'] as const,
  detail: (id: string) => [...epicKeys.details(), id] as const,
  wrapUps: () => [...epicKeys.all, 'wrap-up'] as const,
  wrapUp: (id: string) => [...epicKeys.wrapUps(), id] as const,
};

export function useEpics(filters: EpicFilters = {}) {
  return useInfiniteQuery({
    queryKey: epicKeys.list(filters),
    queryFn: ({ pageParam }) =>
      epicsApi.list({ ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.meta?.hasMore && lastPage.meta.cursor ? lastPage.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Hook for page-by-page epic listing (not infinite scroll).
 * Returns a single page of results for the given cursor.
 */
export function useEpicsPaged(filters: EpicFilters = {}) {
  return useQuery({
    queryKey: [...epicKeys.list(filters), 'paged'] as const,
    queryFn: () => epicsApi.list(filters),
    placeholderData: keepPreviousData,
  });
}

export function useEpic(id: string) {
  return useQuery({
    queryKey: epicKeys.detail(id),
    queryFn: () => epicsApi.get(id),
    enabled: !!id,
    select: (response) => response.data,
  });
}

/** One blueprint with its build facts (new look); checks again every 15s while it runs. */
export function useBlueprint(id: string) {
  return useQuery({
    queryKey: [...epicKeys.detail(id), 'facts'] as const,
    queryFn: () => epicsApi.get(id, { withFacts: true }),
    enabled: !!id,
    select: (response) => response.data,
    refetchInterval: (query) => {
      const facts = query.state.data?.data.facts;
      return facts?.buildJob || facts?.planJob ? 15_000 : false;
    },
  });
}

export function useCreateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEpicInput) => epicsApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      // Also invalidate epic requests since the linked request becomes converted
      queryClient.invalidateQueries({ queryKey: ['epicRequests'] });
    },
  });
}

export function useUpdateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateEpicInput) => epicsApi.update(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useDeleteEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
    },
  });
}

export function useArchiveEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.archive(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useUnarchiveEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.unarchive(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.setQueryData(epicKeys.detail(response.data.id), response);
    },
  });
}

export function useMarkEpicImplemented() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => epicsApi.markImplemented(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      queryClient.invalidateQueries({ queryKey: epicKeys.detail(response.data.id) });
    },
  });
}

/**
 * Hook for transferring an epic between personal and team scope
 */
export function useTransferEpicScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { direction: 'personal-to-team' | 'team-to-personal'; teamId?: string } }) =>
      epicsApi.transfer(id, input),
    onSuccess: (_data, variables) => {
      // Invalidate all epic lists (scope changed)
      void queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      // Invalidate the specific detail
      void queryClient.invalidateQueries({ queryKey: epicKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook for reporting what an implemented epic is missing (ENG-328).
 * Refreshes the epic (follow-up flag) and its issues (the new task).
 */
export function useReportEpicGap(epicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ReportGapInput) => epicsApi.reportGap(epicId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: epicKeys.lists() });
      // The detail may be cached under the id or the identifier (URL param).
      void queryClient.invalidateQueries({ queryKey: epicKeys.details() });
      // Reporting a gap opens follow-up work, which changes the wrap-up display state.
      void queryClient.invalidateQueries({ queryKey: epicKeys.wrapUps() });
      void queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

/**
 * The owner decides a build that asked for a decision is done as it is.
 * Refreshes the epic, its wrap-up and the ideas built from it.
 */
export function useAcceptWrapUp(epicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (note?: string) => epicsApi.acceptWrapUp(epicId, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: epicKeys.all });
      void queryClient.invalidateQueries({ queryKey: epicRequestKeys.all });
    },
  });
}

/**
 * The epic's latest wrap-up and display state (ENG-329). Polls while a
 * wrap-up is running so the banner moves on without a reload.
 */
export function useEpicWrapUp(epicId: string, enabled = true) {
  return useQuery({
    queryKey: epicKeys.wrapUp(epicId),
    queryFn: () => epicsApi.getWrapUp(epicId),
    enabled: enabled && !!epicId,
    refetchInterval: (query) => (query.state.data?.displayState === 'running' ? 15_000 : false),
  });
}
